/**
 * 日期格式化工具函数
 * 统一处理后端返回的ISO格式日期字符串
 * 
 * 输入示例：
 * - "2025-07-15T20:21:28.970259+08:00"
 * - "2025-07-16T15:43:51.953243+08:00"
 * 
 * 使用示例：
 * ```typescript
 * import { formatDateTime, formatRelativeTime } from '@/utils/dateFormatter'
 * 
 * // 标准格式
 * formatDateTime("2025-07-15T20:21:28.970259+08:00") // "2025-07-15 20:21:28"
 * 
 * // 自定义格式
 * formatDateTime("2025-07-15T20:21:28.970259+08:00", "YYYY年MM月DD日") // "2025年07月15日"
 * 
 * // 相对时间
 * formatRelativeTime("2025-07-16T15:43:51.953243+08:00") // "2小时前"
 * ```
 */

/**
 * 格式化日期时间
 * @param dateString ISO格式的日期字符串（后端返回带时区信息，如 "2025-01-11T15:25:00+08:00"）
 * @param format 格式化模式，默认为 "YYYY-MM-DD HH:mm:ss"
 * @returns 格式化后的日期字符串
 * 
 * 注意：后端返回的时间已包含时区信息（+08:00），前端直接使用即可
 */
export const formatDateTime = (dateString: string | null | undefined, format: string = "YYYY-MM-DD HH:mm:ss"): string => {
  if (!dateString) return '未知'

  try {
    const date = new Date(dateString)

    // 获取日期组件（使用后端返回的时区）
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')

    // 根据格式参数进行替换
    return format
      .replace('YYYY', String(year))
      .replace('MM', month)
      .replace('DD', day)
      .replace('HH', hours)
      .replace('mm', minutes)
      .replace('ss', seconds)
      .replace('年', '年')
      .replace('月', '月')
      .replace('日', '日')
  } catch (error) {
    console.error('日期格式化失败:', error)
    return '未知'
  }
}

/**
 * 格式化为相对时间
 * @param dateString ISO格式的日期字符串
 * @returns 相对时间字符串，如："3分钟前"、"2小时前"、"昨天"等，超过7天显示具体日期
 */
export const formatRelativeTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '未知'

  try {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now.getTime() - date.getTime()

    // 转换为秒
    const seconds = Math.floor(diff / 1000)

    if (seconds < 60) {
      return '刚刚'
    }

    // 转换为分钟
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) {
      return `${minutes}分钟前`
    }

    // 转换为小时
    const hours = Math.floor(minutes / 60)
    if (hours < 24) {
      return `${hours}小时前`
    }

    // 转换为天
    const days = Math.floor(hours / 24)
    if (days === 1) {
      return '昨天'
    }
    if (days === 2) {
      return '前天'
    }

    // 超过3天显示具体日期
    return formatDateTime(dateString, "YYYY-MM-DD HH:mm")
  } catch (error) {
    console.error('相对时间格式化失败:', error)
    return '未知'
  }
} 